import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, AppBar, Avatar, Box, Button, Chip, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link,
  List, ListItem, ListItemButton, Paper, Stack, Toolbar, Tooltip, Typography,
} from '@mui/material';
import {
  ApartmentOutlined, ArrowForward, DarkModeOutlined, HelpOutline,
  LightModeOutlined, LockOutlined,
} from '@mui/icons-material';
import { useColorMode } from '../ColorModeContext';
import { palette } from '../ui-theme';

interface SignInTenant {
  tenantId: string;
  displayName: string;
  defaultDomain: string;
}

interface SignInOptions {
  environment: string;
  multiTenantEnabled: boolean;
  tenants: SignInTenant[];
}

const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isTenant(value: unknown): value is SignInTenant {
  return typeof value === 'object' && value !== null
    && 'tenantId' in value && typeof value.tenantId === 'string' && guidPattern.test(value.tenantId)
    && 'displayName' in value && typeof value.displayName === 'string' && value.displayName.trim().length > 0
    && 'defaultDomain' in value && typeof value.defaultDomain === 'string' && value.defaultDomain.trim().length > 0;
}

function isOptions(value: unknown): value is SignInOptions {
  return typeof value === 'object' && value !== null
    && 'environment' in value && typeof value.environment === 'string'
    && 'multiTenantEnabled' in value && typeof value.multiTenantEnabled === 'boolean'
    && 'tenants' in value && Array.isArray(value.tenants) && value.tenants.every(isTenant)
    && (value.multiTenantEnabled ? value.tenants.length > 0 : value.tenants.length === 0)
    && new Set(value.tenants.map((tenant) => tenant.tenantId.toLowerCase())).size === value.tenants.length;
}

const SignInPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { mode, toggleColorMode } = useColorMode();
  const [options, setOptions] = React.useState<SignInOptions | null>(null);
  const [failed, setFailed] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  const [helpOpen, setHelpOpen] = React.useState(false);

  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Sign in | AskIAM';
    return () => { document.title = previousTitle; };
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    setOptions(null);
    setFailed(false);
    const load = async () => {
      try {
        const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(10_000)]);
        const response = await fetch('/api/auth/sign-in-options', { credentials: 'same-origin', signal });
        if (!response.ok) throw new Error('Sign-in options unavailable');
        const data: unknown = await response.json();
        if (!isOptions(data)) throw new Error('Invalid sign-in options');
        if (!controller.signal.aborted) setOptions(data);
      } catch {
        if (!controller.signal.aborted) setFailed(true);
      }
    };
    void load();
    return () => controller.abort();
  }, [attempt]);

  const loginHref = (tenantId?: string) => {
    const query = new URLSearchParams({ returnUrl: searchParams.get('returnUrl') ?? '/' });
    if (tenantId) query.set('tenantId', tenantId);
    if (searchParams.get('switchAccount') === 'true') query.set('switchAccount', 'true');
    // Only the BFF can validate the destination and initiate an identity challenge.
    return '/api/auth/login?' + query.toString();
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{ bgcolor: palette.header.background, color: palette.header.text, borderRadius: 0 }}
      >
        <Toolbar sx={{ gap: 1.5, px: { xs: 2, sm: 4 } }}>
          <Box component="img" src="/askiam-icon.png" alt="" sx={{ width: 32, height: 32 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>AskIAM</Typography>
          <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>Identity and Access Support Portal</Typography>
          <Box sx={{ flexGrow: 1 }} />
          {options?.environment && <Chip label={options.environment} variant="outlined" size="small" sx={{ color: 'inherit', borderColor: 'currentColor' }} />}
          <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <IconButton color="inherit" onClick={toggleColorMode}
              aria-label={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
              {mode === 'light' ? <DarkModeOutlined /> : <LightModeOutlined />}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="md" sx={{ flexGrow: 1, py: { xs: 4, sm: 7 } }}>
        <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'action.hover', color: 'primary.main' }}>
            <LockOutlined fontSize="large" />
          </Avatar>
          <Typography variant="h3" component="h1">Welcome to AskIAM</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 560 }}>
            {options?.multiTenantEnabled === false
              ? 'Sign in with your organizational account to continue.'
              : 'Choose the organization you want to work in. We will take you to Microsoft to sign in securely.'}
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, maxWidth: 680, mx: 'auto' }}>
          <Typography variant="h5" component="h2" id="tenant-selection-heading" sx={{ mb: 1 }}>
            {options?.multiTenantEnabled === false ? 'Your account, your access' : 'Choose your tenant'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {options?.multiTenantEnabled === false
              ? 'Your organization manages the accounts and permissions for this workspace.'
              : 'A tenant is your organization\'s identity directory. Use an account that belongs to the directory you select.'}
          </Typography>

          {failed ? (
            <Alert severity="error" action={
              <Button color="inherit" onClick={() => setAttempt((value) => value + 1)}>Retry</Button>
            }>
              We couldn&apos;t load the sign-in options. Please retry. If this continues, contact your AskIAM administrator.
            </Alert>
          ) : !options ? (
            <Stack direction="row" alignItems="center" spacing={2} role="status" aria-live="polite" sx={{ py: 3 }}>
              <CircularProgress size={24} aria-label="Loading sign-in options" />
              <Typography>Loading your sign-in options...</Typography>
            </Stack>
          ) : options.multiTenantEnabled ? (
            <List disablePadding aria-labelledby="tenant-selection-heading">
              {options.tenants.map((tenant) => (
                <ListItem disablePadding key={tenant.tenantId} sx={{ mb: 1.5 }}>
                  <ListItemButton component="a" href={loginHref(tenant.tenantId)}
                    sx={{ p: 2, gap: 2, border: 1, borderColor: 'divider', borderRadius: 1,
                      '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}>
                    <Avatar variant="rounded" sx={{ bgcolor: 'action.hover', color: 'primary.main' }}>
                      <ApartmentOutlined />
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{tenant.displayName}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{tenant.defaultDomain}</Typography>
                    </Box>
                    <ArrowForward color="primary" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Button component="a" href={loginHref()} variant="contained" fullWidth endIcon={<ArrowForward />}>
              Continue to Microsoft sign-in
            </Button>
          )}

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3 }}>
            <HelpOutline color="primary" fontSize="small" />
            <Link component="button" variant="body2" onClick={() => setHelpOpen(true)}>
              Why does tenant selection matter?
            </Link>
          </Stack>
        </Paper>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
          Not sure which tenant to use? Your organization&apos;s AskIAM administrator can help.
        </Typography>
      </Container>

      <Box component="footer" sx={{ px: 2, pb: 3, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">Secure sign-in with Microsoft Entra ID</Typography>
      </Box>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} aria-labelledby="tenant-help-title" maxWidth="sm" fullWidth>
        <DialogTitle id="tenant-help-title">Why tenant selection matters</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Each tenant has its own accounts and access rules. A familiar email address or display name does not, by itself, identify the directory you need.
          </Typography>
          {options?.multiTenantEnabled && (
            <Typography paragraph>
              Choose the tenant where your account is a native member. Guest accounts from another directory cannot sign in to this multi-tenant workspace.
            </Typography>
          )}
          <Typography paragraph>
            Your choice tells Microsoft which directory to use. AskIAM then checks that the signed-in identity belongs to that tenant and has the required access. Selecting a tenant does not grant permissions.
          </Typography>
          <Typography>
            The domain under each name helps identify the directory, even when a friendly alias is shown. If you are unsure, check with your AskIAM administrator before continuing.
          </Typography>
        </DialogContent>
        <DialogActions><Button autoFocus onClick={() => setHelpOpen(false)}>Got it</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignInPage;
